/**
 * Simple HeartbeatService for presence tracking
 */
class HeartbeatService {
    constructor() {
        this.timer = null
        this.config = null
        this.cid = null
        this.isStarting = false
    }

    /**
     * Start sending heartbeats
     */
    start(config) {
        if (this.isStarting) return
        this.isStarting = true

        try {
            this.stop() // Stop any existing heartbeat

            this.config = {
                endpoint: '/pusher-beat',
                intervalMs: 60000,
                screenId: null,
                userId: null,
                layer: 'overlay',
                // Read at each beat rather than passed once, because the whole
                // point is the value that changes between beats. Owned by the
                // caller: this service knows nothing about Pusher.
                state: null,
                ...config
            }

            // Send initial heartbeat
            this._send({ reason: 'start' })

            // Setup interval (minimum 15 seconds)
            const interval = Math.max(15000, this.config.intervalMs)
            this.timer = setInterval(() => {
                this._send({ reason: 'tick' })
            }, interval)

            // Send final beat on page unload
            const unloadHandler = () => this._send({ reason: 'unload', closing: true })
            window.addEventListener('beforeunload', unloadHandler)
            window.addEventListener('pagehide', unloadHandler)

            // Store cleanup function
            this.cleanup = () => {
                window.removeEventListener('beforeunload', unloadHandler)
                window.removeEventListener('pagehide', unloadHandler)
            }
        } finally {
            this.isStarting = false
        }
    }

    /**
     * Stop sending heartbeats
     */
    stop() {
        if (this.timer) {
            clearInterval(this.timer)
            this.timer = null
        }

        if (this.cleanup) {
            this.cleanup()
            this.cleanup = null
        }

        this.config = null
    }

    /**
     * Whatever the caller wants recorded about the socket, as of this beat.
     *
     * Wrapped, because a heartbeat that stops over a bad accessor takes the only
     * evidence that the layer is alive with it.
     */
    _state() {
        try {
            return (this.config && this.config.state) ? this.config.state() || {} : {}
        } catch (e) {
            return { socket_state: 'unreadable' }
        }
    }

    /**
     * Send a heartbeat
     */
    async _send(extra = {}) {
        if (!this.config) return

        const isInitial = extra.reason === 'start'

        const payload = JSON.stringify({
            screenId: this.config.screenId,
            userId: this.config.userId,
            layer: this.config.layer,
            ts: Date.now(),
            cid: this.cid,
            route: this.config.route || null,
            ua: navigator.userAgent,
            width: window.innerWidth,
            height: window.innerHeight,
            visibility: document.visibilityState,
            online: navigator.onLine,
            ...this._state(),
            ...extra
        })

        try {
            if (isInitial) {
                const res = await fetch(this.config.endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: payload,
                    keepalive: true
                })
                const json = await res.json()
                if (json && json.cid) this.cid = json.cid
            } else if (this.cid) {
                const blob = new Blob([payload], { type: 'application/json' })
                const queued = navigator.sendBeacon && navigator.sendBeacon(this.config.endpoint, blob)
                if (!queued) {
                    fetch(this.config.endpoint, {
                        method: 'POST',
                        headers: {'Content-Type': 'application/json'},
                        body: payload,
                        keepalive: true
                    })
                }
            }
        } catch (e) {
            fetch(this.config.endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload,
                keepalive: true
            }).catch(() => {})
        }
    }
}

export default HeartbeatService